import { BASE_URL, fetchWithAuth } from './client';

/**
 * Billing: create a customer.
 */
export async function createBillingCustomer(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/billing/create-customer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error("Unable to create customer");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Billing: create setup intent for payment method capture.
 */
export async function createSetupIntent(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/billing/create-setup-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error("Unable to create setup intent");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Billing: create subscription.
 */
export async function createSubscription(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/billing/create-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error("Unable to create subscription");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Billing: start Stripe Checkout (preferred flow).
 * RQ: { lookupKey: string, successUrl?: string, cancelUrl?: string }
 * RS: { status: 200, data: { url: string } }
 */
export async function createCheckoutSession(payload) {
    // Build request body based on what was provided
    const requestBody = { ...payload };

    // If using lookupKey, convert to stripeLookupKey
    if (payload.lookupKey && !payload.stripeLookupKey && !payload.stripePriceId) {
        requestBody.stripeLookupKey = payload.lookupKey;
        delete requestBody.lookupKey;
    }
    // If lookupKey exists alongside stripeLookupKey or stripePriceId, remove it
    else if (requestBody.lookupKey) {
        delete requestBody.lookupKey;
    }

    // Ensure we're using either stripePriceId or stripeLookupKey
    if (!requestBody.stripePriceId && !requestBody.stripeLookupKey) {
        console.error('❌ [Billing] Missing both stripePriceId and stripeLookupKey');
        throw new Error("Invalid checkout configuration");
    }

    console.log('🔍 [Billing] Creating checkout session with payload:', JSON.stringify(requestBody, null, 2));

    const response = await fetchWithAuth(`${BASE_URL}/api/billing/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Billing] Checkout session failed:', response.status, errorText);

        // Try to parse error details
        let errorMessage = "Unable to start checkout. Please try again.";
        try {
            const errorData = JSON.parse(errorText);
            if (errorData.detail && errorData.detail.includes('No Stripe price found for lookup key')) {
                errorMessage = `Stripe configuration error: ${errorData.detail}. Please contact support.`;
            }
        } catch (e) {
            // Keep default error message
        }

        throw new Error(errorMessage);
    }
    const data = await response.json();
    console.log('✅ [Billing] Checkout session created:', data);
    return { status: response.status, data };
}

/**
 * Billing: open Stripe Customer Portal.
 * RQ: { sessionId?: string, customerId?: string }
 * RS: { status: 200, data: { url: string } }
 */
export async function createPortalSession(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/billing/portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error("Unable to open billing portal. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Subscriptions: get current user's subscription details.
 * RQ: GET /api/subscriptions/me
 * RS: { status: 200, data: { userId, tier, status, aiCreditsRemaining, plan, ... } }
 */
export async function fetchUserSubscription() {
    const url = `${BASE_URL}/api/subscriptions/me`;
    console.log("🔍 Fetching subscription from backend:", url);

    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });

    console.log("📥 Subscription API response status:", response.status);

    if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Subscription API failed:", response.status, errorText);
        throw new Error(`Unable to fetch subscription details (${response.status}). Please try again.`);
    }

    const data = await response.json();
    console.log("✅ Subscription data received:", JSON.stringify(data, null, 2));
    console.log("💰 Credits breakdown:", {
        "data.aiCreditsRemaining": data?.aiCreditsRemaining,
        "data.data.aiCreditsRemaining": data?.data?.aiCreditsRemaining,
        "Full data structure": data
    });
    return { status: response.status, data };
}

/**
 * Subscriptions: deduct credits from current user's balance.
 * RQ: POST /api/subscriptions/me/credits/deduct
 * Body: { amount: number }
 * RS: { status: 200, data: { aiCreditsRemaining: number, ... } }
 */
export async function deductSubscriptionCredits(amount) {
    const response = await fetchWithAuth(`${BASE_URL}/api/subscriptions/me/credits/deduct?amount=${encodeURIComponent(amount)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
    });
    if (!response.ok) {
        throw new Error("Unable to deduct credits. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}
